/*
  File: src/cart/CartContext.js
  Purpose: Provide a shopping cart state container with persistence and backend sync.
  Responsibilities:
  - Persist cart items in localStorage under cart:items:v1.
  - For authenticated users, debounce-sync items to backend /carts/sync.
  - Hydrate from latest server cart on login and enrich items with product details.
  - Expose imperative actions: addItem, updateQty, removeItem, clear and derived total.
  Data model per item:
  { id, name, display_name?, price, currency, imageUrl, size, color?, variantId?, qty }
  Notes:
  - addItem eagerly syncs and then enriches with canonical product fields.
  - All sync calls are best-effort; failures are logged but non-blocking for UX.
*/
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../auth/api';
import { useAuth } from '../auth/AuthContext';

const CartContext = createContext({
  items: [],
  addItem: (_item) => { },
  updateQty: (_id, _size, _color, _qty) => { },
  removeItem: (_id, _size, _color) => { },
  clear: () => { },
  total: 0,
  paymentMethod: 'card',
  setPaymentMethod: (_m) => { },
});

const STORAGE_KEY = 'cart:items:v1';

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const { user } = useAuth();
  const DEBUG_CART = String(process.env.REACT_APP_DEBUG_CART || '').toLowerCase() === 'true';

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch { }
  }, []);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { }
  }, [items]);

  // Sync cart to database whenever items change (for logged in users)
  useEffect(() => {
    if (!user) return;

    const syncToDatabase = async () => {
      try {
        if (DEBUG_CART) console.log('Syncing cart to database:', { user_id: user.id, items: items.map(i => ({ product_id: i.id, qty: i.qty })) });
        const response = await apiClient.post('/carts/sync', {
          user_id: user.id,
          items: items.map(i => ({ product_id: i.id, qty: i.qty }))
        });
        if (DEBUG_CART) console.log('Cart synced successfully:', response.data);
      } catch (e) {
        console.error('Failed to sync cart to database');
        if (DEBUG_CART) console.error('Error details:', e.response?.data || e.message);
      }
    };

    // Debounce the sync to avoid too many API calls
    const timeoutId = setTimeout(syncToDatabase, 1000);
    return () => clearTimeout(timeoutId);
  }, [items, user]);

  // Reconcile any existing items to ensure long product name (p.name) is used
  useEffect(() => {
    const reconcile = async () => {
      if (!items || items.length === 0) return;
      try {
        const uniqueProductIds = Array.from(new Set(items.map(i => i.id)));
        const idToProduct = new Map();
        await Promise.all(uniqueProductIds.map(async (pid) => {
          try {
            const { data } = await apiClient.get(`/products/${pid}`);
            const p = data.items ? data.items[0] : data;
            if (p) idToProduct.set(pid, p);
          } catch { }
        }));
        if (idToProduct.size === 0) return;
        setItems(prev => prev.map(i => {
          const p = idToProduct.get(i.id);
          if (!p) return i;
          return {
            ...i,
            // Force long name from products.name; keep short in display_name if needed elsewhere
            name: p.name || i.name,
            display_name: p.display_name ?? i.display_name,
            price: typeof p.price_cents === 'number' ? p.price_cents / 100 : i.price,
            currency: p.currency || i.currency,
            imageUrl: p.image_url || i.imageUrl,
          };
        }));
      } catch { }
    };
    reconcile();
  }, [user]);

  // Hydrate from backend if logged in
  useEffect(() => {
    const hydrate = async () => {
      if (!user) return;
      try {
        if (DEBUG_CART) console.log('Hydrating cart for user:', user.id);
        // fetch latest cart for user
        const res = await apiClient.get('/carts', { params: { user_id: user.id, limit: 1, offset: 0 } });
        const carts = res.data?.items || [];
        const latest = carts[0];
        if (!latest) {
          if (DEBUG_CART) console.log('No cart found for user');
          return;
        }
        if (DEBUG_CART) console.log('Found cart:', latest.id);
        const ciRes = await apiClient.get('/cart-items', { params: { cart_id: latest.id, limit: 500, offset: 0 } });
        const cartItems = ciRes.data?.items || [];
        if (cartItems.length === 0) {
          if (DEBUG_CART) console.log('No items in cart');
          return;
        }
        if (DEBUG_CART) console.log('Found cart items:', cartItems.length);
        // fetch product details for each product_id
        const productDetails = await Promise.all(cartItems.map(async (ci) => {
          try {
            const p = await apiClient.get(`/products/${ci.product_id}`);
            return { ci, p: p.data };
          } catch (_e) {
            return { ci, p: null };
          }
        }));
        const mapped = productDetails.map(({ ci, p }) => ({
          id: ci.product_id,
          variantId: ci.variant_id || null,
          name: p?.name || p?.display_name || `Product #${ci.product_id}`,
          display_name: p?.display_name || undefined,
          price: typeof p?.price_cents === 'number' ? (p.price_cents / 100) : 0,
          currency: p?.currency || '$',
          imageUrl: p?.image_url || '',
          size: ci.size || 'M',
          qty: ci.qty,
        }));
        if (DEBUG_CART) console.log('Hydrated cart items:', mapped);
        setItems(mapped);
      } catch (e) {
        console.error('Failed to hydrate cart');
        if (DEBUG_CART) console.error(e);
      }
    };
    hydrate();
  }, [user]);

  // Add to cart handles uniqueness by productId, variantId, size, color!
  const addItem = ({ id, name, price, currency, imageUrl, size = 'M', color = null, variantId = null, qty = 1 }) => {
    setItems((prev) => {
      // Uniqueness: ALL of id, variantId, size, color!
      const existingIdx = prev.findIndex(i => (
        i.id === id &&
        i.size === size &&
        String(i.color || '') === String(color || '') &&
        String(i.variantId || '') === String(variantId || '')
      ));
      let next;
      if (existingIdx >= 0) {
        next = [...prev];
        next[existingIdx] = { ...next[existingIdx], qty: next[existingIdx].qty + qty };
      } else {
        next = [...prev, { id, name, price, currency, imageUrl, size, color, variantId, qty }];
      }

      // Always transmit variantId in sync payload!
      (async () => {
        try {
          if (!user) return;
          if (DEBUG_CART) console.log('[CartContext] Syncing cart to backend with items:', next.map(i => ({ product_id: i.id, variant_id: i.variantId, size: i.size, color: i.color, qty: i.qty })));
          await apiClient.post('/carts/sync', {
            user_id: user.id,
            items: next.map(i => ({ product_id: i.id, variant_id: i.variantId ?? null, qty: i.qty, size: i.size || 'M' }))
          });
        } catch (e) {
          console.error('Failed to sync cart:', e);
        }
      })();

      return next;
    });
    // Display enrichment logic here unchanged
    (async () => {
      try {
        const { data } = await apiClient.get(`/products/${id}`);
        const p = data.items ? data.items[0] : data;
        if (!p) return;
        setItems((prev) => prev.map(i => {
          if (i.id === id && i.size === size && String(i.color || '') === String(color || '') && String(i.variantId || '') === String(variantId || '')) {
            return {
              ...i,
              display_name: p.display_name || i.display_name,
              name: p.name || i.name,
              price: typeof p.price_cents === 'number' ? (p.price_cents / 100) : i.price,
              currency: p.currency || i.currency,
              // Keep variant-specific image if already present
              imageUrl: i.imageUrl || p.image_url || '',
            };
          }
          return i;
        }));
      } catch (_e) { }
    })();
  };

  const updateQty = (id, size, color, variantId, qty) => {
    setItems((prev) => {
      const next = prev.map(i => (i.id === id && i.size === size && String(i.color || '') === String(color || '') && String(i.variantId || '') === String(variantId || '') ? { ...i, qty: Math.max(1, qty) } : i));
      // Persist to DB (best-effort)
      (async () => {
        try {
          if (!user) return;
          if (DEBUG_CART) console.log('[CartContext] UpdateQty sync. Items:', next.map(i => ({ product_id: i.id, variant_id: i.variantId, size: i.size, color: i.color, qty: i.qty })));
          await apiClient.post('/carts/sync', {
            user_id: user.id,
            items: next.map(i => ({ product_id: i.id, variant_id: i.variantId || null, qty: i.qty, size: i.size || 'M' }))
          });
        } catch { }
      })();
      return next;
    });
  };

  const removeItem = (id, size, color, variantId) => {
    setItems((prev) => {
      const next = prev.filter((i) => {
        const sameId = String(i.id) === String(id);
        if (!sameId) return true;
        if (!i.size || !size) return false;
        const sizeDiff = i.size !== size;
        const colorDiff = String(i.color || '') !== String(color || '');
        const variantDiff = String(i.variantId || '') !== String(variantId || '');
        return sizeDiff || colorDiff || variantDiff;
      });
      // Persist to DB (best-effort)
      (async () => {
        try {
          if (!user) return;
          if (DEBUG_CART) console.log('[CartContext] RemoveItem sync. Items:', next.map(i => ({ product_id: i.id, variant_id: i.variantId, size: i.size, color: i.color, qty: i.qty })));
          await apiClient.post('/carts/sync', {
            user_id: user.id,
            items: next.map(i => ({ product_id: i.id, variant_id: i.variantId || null, qty: i.qty, size: i.size || 'M' }))
          });
        } catch { }
      })();
      return next;
    });
  };

  const clear = () => {
    setItems([]);
    (async () => {
      try {
        if (!user) return;
        await apiClient.post('/carts/sync', { user_id: user.id, items: [] });
      } catch { }
    })();
  };

  const total = useMemo(() => items.reduce((sum, i) => sum + i.price * i.qty, 0), [items]);

  const value = useMemo(() => ({ items, addItem, updateQty, removeItem, clear, total, paymentMethod, setPaymentMethod }), [items, total, paymentMethod]);

  return (
    <CartContext.Provider value={value}>{children}</CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
